import React, { useEffect, useState } from 'react';
import { FEATURED_POST_ID, SOCIAL_POSTS } from './socialPosts';
import api from '../../services/api';

const RECENT_POST_COUNT = 4;

const PLATFORMS = {
  instagram: {
    label: 'Instagram',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  x: {
    label: 'X',
    icon: (
      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
      </svg>
    ),
  },
};

const formatDate = (isoDate) =>
  new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

// Opens the original post in a new tab; renders without a link until a URL is set.
const PostLink = ({ url, className, children }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`${className} rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent`}>
      {children}
    </a>
  ) : (
    <div className={className}>{children}</div>
  );

// The post's image, or a branded graphic when there isn't one. Graphics with
// text (fit: 'contain') show in full over a blurred copy of themselves.
const PostVisual = ({ post, large = false }) => {
  if (post.image && post.fit === 'contain') {
    return (
      <div className="relative w-full h-full transition-transform duration-500 group-hover:scale-105">
        <img src={post.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-xl opacity-40 scale-110" />
        <img src={post.image} alt={post.headline} className="relative w-full h-full object-contain" />
      </div>
    );
  }
  if (post.image) {
    return (
      <img
        src={post.image}
        alt={post.headline}
        style={post.focus ? { objectPosition: post.focus } : undefined}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    );
  }
  return (
    <div className="w-full h-full bg-jp-navy flex flex-col justify-end p-3 sm:p-4 transition-transform duration-500 group-hover:scale-105">
      <span className={`text-yellow-300 font-bold uppercase tracking-widest ${large ? 'text-xs mb-1' : 'text-[8px]'}`}>
        {post.graphic?.kicker}
      </span>
      <span className={`font-anton text-white leading-none ${large ? 'text-5xl' : 'text-base sm:text-lg'}`}>
        {post.graphic?.title}
      </span>
    </div>
  );
};

const PlatformTag = ({ platform }) => {
  const info = PLATFORMS[platform];
  if (!info) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {info.icon}
      {info.label}
    </span>
  );
};

// Posts added from the admin panel's Home Page tab, newest first. Thumbnails
// are shown whole (fit: 'contain') since they're often graphics with text.
const toUploadedPost = (post) => ({
  id: post.id,
  headline: post.headline,
  date: post.date ? post.date.slice(0, 10) : '',
  platform: post.platform,
  url: post.url,
  image: post.image,
  fit: 'contain',
});

export const SocialSection = () => {
  const [uploadedPosts, setUploadedPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/public/social-posts')
      .then(({ data }) => {
        if (!cancelled) setUploadedPosts((data.posts || []).map(toUploadedPost));
      })
      .catch(() => {}); // keep showing the built-in posts
    return () => {
      cancelled = true;
    };
  }, []);

  // The newest uploaded post is featured; built-in posts fill in after them.
  const posts = [...uploadedPosts, ...SOCIAL_POSTS];
  const featured = uploadedPosts[0] || SOCIAL_POSTS.find((post) => post.id === FEATURED_POST_ID) || posts[0];
  const recent = posts.filter((post) => post !== featured).slice(0, RECENT_POST_COUNT);

  if (!featured) return null;

  return (
    <section className="bg-jp-page px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div className="max-w-2xl">
            <h2 className="font-anton text-4xl sm:text-5xl leading-[1.05] tracking-tight text-jp-ink mb-3">
              FOLLOW <span className="text-jp-accent">JUCO PIPELINE</span>
            </h2>
            <p className="text-jp-muted text-sm sm:text-base leading-relaxed">
              Rankings, player spotlights, recruiting updates, and everything happening across JUCO baseball.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-10">
          {/* Featured post */}
          <PostLink url={featured.url} className="group block">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-jp-navy mb-5">
              <PostVisual post={featured} large />
            </div>
            {featured.category && (
              <p className="text-jp-gold-ink text-xs font-bold uppercase tracking-widest mb-2">
                {featured.category}
              </p>
            )}
            <h3 className="text-2xl sm:text-3xl font-extrabold text-jp-ink leading-tight mb-3 transition-colors duration-300 group-hover:text-jp-accent">
              {featured.headline}
            </h3>
            {featured.description && <p className="text-jp-muted leading-relaxed mb-4 max-w-2xl">{featured.description}</p>}
            <div className="flex items-center gap-4 text-sm">
              {featured.date && <span className="text-jp-muted">{formatDate(featured.date)}</span>}
              <span className="text-jp-muted">
                <PlatformTag platform={featured.platform} />
              </span>
              <span className="ml-auto font-semibold text-jp-accent-ink inline-flex items-center gap-1.5">
                View Post
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m0 0l-6-6m6 6l-6 6" />
                </svg>
              </span>
            </div>
          </PostLink>

          {/* Recent posts */}
          <div>
            <p className="text-jp-muted text-xs font-bold uppercase tracking-widest mb-2">Recent</p>
            <div className="divide-y divide-jp-line">
              {recent.map((post) => (
                <PostLink key={post.id} url={post.url} className="group flex gap-4 py-4 first:pt-2">
                  <div className="w-28 h-20 sm:w-32 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden bg-jp-navy">
                    <PostVisual post={post} />
                  </div>
                  <div className="min-w-0 flex flex-col">
                    {post.category && (
                      <p className="text-jp-gold-ink text-[10px] font-bold uppercase tracking-widest mb-1">
                        {post.category}
                      </p>
                    )}
                    <h4 className="text-jp-ink font-bold leading-snug line-clamp-2 transition-colors duration-300 group-hover:text-jp-accent">
                      {post.headline}
                    </h4>
                    <p className="mt-auto pt-1 text-jp-muted text-xs flex items-center gap-3">
                      <PlatformTag platform={post.platform} />
                      {post.date && <span>{formatDate(post.date)}</span>}
                    </p>
                  </div>
                </PostLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialSection;
